import React from "react";
import { User, Shield, Bell, CreditCard, History, Gift, MonitorSmartphone } from "lucide-react";
import { SettingsAccent } from "./components/settingsConfig";

export type SettingsSection = {
  id: string;
  title: string;
  description: string;
  path: string;
  icon: React.ReactNode;
  accent: SettingsAccent;
  priority: "high" | "medium" | "low";
  category?: string;
};

/**
 * The personal settings pages. Titles are what the sidebar, page headers,
 * breadcrumbs and the command palette show, so keep them in sentence case.
 * `/settings` itself redirects to Profile, and Delete account lives at the
 * bottom of Profile (`/settings/account` redirects there).
 */
export const SETTINGS_SECTIONS: SettingsSection[] = [
  {
    id: "profile",
    title: "Profile",
    description: "Manage your personal information and preferences",
    icon: <User className="w-4 h-4" />,
    path: "/settings/profile",
    accent: "indigo",
    priority: "high",
    category: "account",
  },
  {
    id: "notifications-list",
    title: "Notifications",
    description: "View and manage your notifications",
    icon: <Bell className="w-4 h-4" />,
    path: "/settings/notifications",
    accent: "violet",
    priority: "low",
    category: "account",
  },
  {
    id: "security",
    title: "Password and sign-in",
    description: "Password, two-factor authentication, passkeys and sign-in methods",
    icon: <Shield className="w-4 h-4" />,
    path: "/settings/security/account",
    accent: "emerald",
    priority: "high",
    category: "security",
  },
  {
    id: "sessions",
    title: "Sessions",
    description: "Review devices signed in to your account and revoke access",
    icon: <MonitorSmartphone className="w-4 h-4" />,
    path: "/settings/security/sessions",
    accent: "emerald",
    priority: "medium",
    category: "security",
  },
  {
    id: "login-history",
    title: "Sign-in history",
    description: "View your account sign-in history and activity",
    icon: <History className="w-4 h-4" />,
    path: "/settings/security/login-history",
    accent: "emerald",
    priority: "medium",
    category: "security",
  },
  {
    id: "billing",
    title: "Billing",
    description: "Manage your subscription and payment methods",
    icon: <CreditCard className="w-4 h-4" />,
    path: "/settings/billing",
    accent: "cyan",
    priority: "low",
    category: "billing",
  },
  {
    id: "referrals",
    title: "Referrals",
    description:
      "Share your link; bonus project licenses can apply when referrals complete qualifying purchases",
    icon: <Gift className="w-4 h-4" />,
    path: "/settings/referrals",
    accent: "violet",
    priority: "low",
    category: "billing",
  },
];

// Keyed map for O(1) lookups by id
export const SETTINGS_SECTIONS_MAP: Record<string, SettingsSection> = Object.fromEntries(
  SETTINGS_SECTIONS.map((section) => [section.id, section])
) as Record<string, SettingsSection>;

export const getSection = (id: string): SettingsSection | undefined => SETTINGS_SECTIONS_MAP[id];

export const getAccent = (id: string) => getSection(id)?.accent;
